import he from 'he';

import createEnum from 'scripts/lib/create_enum';

import createModel, { prop } from './lib/create_model';

// Deliberately not an `oneOf` prop below: oneOf violations throw during model
// construction in dev builds, so a source type from a newer server would abort
// the gateway observer instead of degrading to a plain label in the UI.
const CitationType = createEnum('answer', 'document', 'product', 'web_page');

const TeamAssistCitation = createModel({
  modelName: 'TeamAssistCitation',

  properties: {
    // Answers only.
    snippetId: prop(String),
    snippetName: prop(String),

    excerpt: prop(String),
    label: prop(String),
    // Where in the source the passage sits, e.g. "p. 4".
    location: prop(String),
    type: prop(String),
    // Empty when the source has no address to open — always for an uploaded
    // document, and for a web page whose URL the provider stripped.
    url: prop(String),
  },

  // Messages persisted before knowledge sources became citable carry no type.
  isAnswer() {
    return !this.type || this.type === CitationType.answer;
  },

  // Excludes products: a product's excerpt is its marketing description, not a
  // passage that evidences the claim.
  hasPassage() {
    const isPassageType = this.type === CitationType.document || this.type === CitationType.web_page;
    return isPassageType && Boolean(this.excerpt || this.location);
  },

  // snippetName covers Answer citations persisted before `label` existed.
  getLabel() {
    return this.label || this.snippetName || this.snippetId || '';
  },

  hasSafeUrl() {
    return Boolean(this.url) && TeamAssistCitation.isSafeUrl(this.url);
  },

  statics: {
    Type: CitationType,

    // Backend-authored text arrives entity-encoded (e.g. "Crate &amp; Barrel").
    // Decoded once here rather than at each render site, so a consumer reads
    // display-ready text off the model and the work happens per message instead
    // of per render.
    overrideFromJs(originalFromJs) {
      return attrs =>
        originalFromJs({
          ...attrs,
          excerpt: decodeEntities(attrs && attrs.excerpt),
          label: decodeEntities(attrs && attrs.label),
          snippetName: decodeEntities(attrs && attrs.snippetName),
        });
    },

    // Citation URLs come from retrieval over org-configured web pages and
    // product feeds, not from a trusted authoring surface. supernova
    // scheme-checks web page URLs but converts catalog products straight
    // through, so a `javascript:` href can reach a renderer. `target`/`rel` are
    // no defense against one. Static so SaganAdmin, which holds plain JSON
    // rather than model instances, can gate on the same rule.
    isSafeUrl(url) {
      try {
        const { protocol } = new URL(url);
        return protocol === 'http:' || protocol === 'https:';
      } catch {
        return false;
      }
    },
  },
});

function decodeEntities(value) {
  return value ? he.decode(value) : value;
}

export default TeamAssistCitation;
